import type {
    CareLogCardArea,
    CareLogCardMaterial,
} from './-care-log-list-card'
import type { MediaItem } from '@/components/common/media-lightbox'
import { MediaThumbStrip } from '@/components/common/media-lightbox'
import { agriUnitLabel } from '@/lib/agri-unit-label'
import { m } from '@/lib/paraglide/messages'

/** Ô rỗng của bảng: gạch ngang, không để trắng — người đọc phân biệt được
 *  "không có dữ liệu" với "cột bị lỗi không render". */
function EmptyMark() {
    return <span className="text-muted-foreground">—</span>
}

/**
 * Cột "Tên bãi": mỗi bãi một dòng kèm diện tích. Nhật ký thường ghi nhiều bãi
 * cùng lúc; nhồi chúng vào một dòng ngăn bằng dấu phẩy thì bảng phải giãn rộng
 * và mắt không tách được bãi nào với bãi nào.
 */
export function CareLogParcelCell({ areas }: { areas: CareLogCardArea[] }) {
    if (areas.length === 0) return <EmptyMark />

    return (
        <div className="space-y-0.5">
            {areas.map((area) => (
                <div
                    key={area.id}
                    data-testid="care-log-parcel-row"
                    className="whitespace-nowrap"
                >
                    {area.name}
                    {area.acreage ? (
                        <span className="ml-1 tabular-nums text-xs text-muted-foreground">
                            {m.care_log_area_acreage({ acreage: area.acreage })}
                        </span>
                    ) : null}
                </div>
            ))}
        </div>
    )
}

/**
 * Cột định lượng: mỗi vật tư một dòng kèm ĐƠN VỊ riêng của nó.
 *
 * Không dùng `care_logs.quantity` (tổng của cả nhật ký) vì nó cộng lẫn đơn vị —
 * 3 chai + 2,5 bao ra 5,5, một con số không có nghĩa. Hái chè cũng đi qua đây:
 * `careLogCardMaterials` đã quy sản lượng thành một dòng kg, nên cả ba loại
 * hoạt động dùng chung một cách hiển thị.
 */
export function CareLogAmountCell({
    materials,
}: {
    materials: CareLogCardMaterial[]
}) {
    if (materials.length === 0) return <EmptyMark />

    return (
        <div className="space-y-0.5">
            {materials.map((mat, i) => (
                <div
                    key={`${mat.name}-${i}`}
                    data-testid="care-log-amount-row"
                    className="flex items-baseline justify-between gap-3"
                >
                    <span className="min-w-0 break-words">
                        {mat.name}
                        {mat.selfBought && (
                            <span className="ml-1 text-xs text-muted-foreground">
                                {m.care_log_self_bought_tag()}
                            </span>
                        )}
                    </span>
                    <span className="shrink-0 whitespace-nowrap font-medium tabular-nums">
                        {mat.quantity}
                        {mat.unit ? ` ${agriUnitLabel(mat.unit)}` : ''}
                    </span>
                </div>
            ))}
        </div>
    )
}

/**
 * Số ô tối đa của cột ảnh: 3 thumbnail + 1 ô "+N" vừa đúng một hàng trong bề
 * rộng cột. Nhật ký chụp 30 tấm mà trải hết ra thì dòng đó cao gấp chục lần các
 * dòng khác, bảng không còn đọc lướt được — bấm "+N" vẫn xem đủ trong popup.
 */
const MAX_EVIDENCE_THUMBS = 4

/**
 * Cột "Ảnh/Video minh chứng": thumbnail của tệp trong dòng, bấm vào mở popup
 * xem cỡ lớn và chuyển qua lại trong ĐỦ bộ tệp của dòng (popup không bị cắt
 * theo dải thumbnail).
 *
 * Popup nằm ở màn (một cái cho cả bảng), không dựng trong từng ô — mỗi dòng một
 * Dialog là hàng trăm cây Radix mount vô ích cho một trang danh sách.
 */
export function CareLogEvidenceCell({
    items,
    onOpen,
}: {
    items: MediaItem[]
    onOpen: (items: MediaItem[], index: number) => void
}) {
    if (items.length === 0) return <EmptyMark />

    return (
        <MediaThumbStrip
            items={items}
            onOpen={(index) => onOpen(items, index)}
            maxThumbs={MAX_EVIDENCE_THUMBS}
            // 4 ô `size-10` + 3 khe `gap-1.5` = 178px, nên bề rộng tối đa phải
            // từ 12rem (192px) trở lên; 11rem là ô "+N" bị đẩy xuống hàng hai,
            // đúng thứ giới hạn này sinh ra để tránh.
            className="max-w-48"
        />
    )
}
